import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { addOutreachItem } from "../../services/outreach/firestore/crud";
import { extractPlaylistId } from "../../services/outreach/integrations/spotify";
import { useSpotifyPlaylistSearch } from "../../hooks/useSpotifyPlaylistSearch";
import {
  OutreachCategory,
  type SpotifyPlaylist,
  type SpotifyCategory,
  type SpotifyPlaylistDetails,
} from "../../types/outreach";
import { SpotifySearchResults } from "./SpotifySearchResults";
import { SpotifyPlaylistDetail } from "./SpotifyPlaylistDetail";
import {
  ChannelConfirmationModal,
  type SpotifyPlaylistFormValues,
} from "./ChannelConfirmationModal";

interface SpotifyPlaylistSearchModalProps {
  open: boolean;
  onClose: () => void;
  existingPlaylists: SpotifyPlaylist[];
  onPlaylistAdded: () => void;
}

const DEFAULT_CATEGORY_NAMES = [
  "Pop",
  "Hip-Hop",
  "Rock",
  "Electronic",
  "Indie",
  "R&B",
  "Country",
  "Latin",
  "Dance",
  "Alternative",
];

type DisplayCategory = SpotifyCategory & { placeholder?: boolean };

const formatCuratorHandle = (displayName?: string): string => {
  if (!displayName) {
    return "";
  }

  const normalized = displayName.replace(/[^a-z0-9]/gi, "");
  if (!normalized) {
    return "";
  }

  return `@${normalized.toLowerCase()}`;
};

const buildPlaylistFormValues = (
  playlist: SpotifyPlaylistDetails,
  genre: string
): SpotifyPlaylistFormValues => ({
  playlistName: playlist.name,
  playlistUrl: playlist.external_urls?.spotify ?? "",
  curatorSocialHandle: formatCuratorHandle(playlist.owner?.display_name),
  curatorEmail: "",
  playlistFollowers: playlist.followers?.total?.toString() ?? "0",
  trackCount: playlist.tracks?.total?.toString() ?? "0",
  genre,
  source: "Spotify Search",
  lastUpdated: new Date().toISOString(),
});

export const SpotifyPlaylistSearchModal: React.FC<
  SpotifyPlaylistSearchModalProps
> = ({ open, onClose, existingPlaylists, onPlaylistAdded }) => {
  const theme = useTheme();
  const {
    searchQuery,
    setSearchQuery,
    activeQuery,
    categories,
    categoriesLoading,
    selectedCategoryId,
    playlists,
    selectedPlaylist,
    loading,
    detailsLoading,
    error,
    fetchCategories,
    selectCategory,
    performSearch,
    fetchPlaylistDetailsById,
    resetSearch,
  } = useSpotifyPlaylistSearch();

  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingPlaylist, setPendingPlaylist] =
    useState<SpotifyPlaylistDetails | null>(null);
  const [initialFormValues, setInitialFormValues] =
    useState<SpotifyPlaylistFormValues | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    void performSearch(undefined, { resetCategory: true });
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  useEffect(() => {
    if (open) {
      void fetchCategories();
    } else {
      resetSearch();
      setConfirmationOpen(false);
      setPendingPlaylist(null);
      setInitialFormValues(null);
      setNotification(null);
    }
  }, [open, fetchCategories, resetSearch]);

  const existingPlaylistIds = useMemo(() => {
    const ids = new Set<string>();

    existingPlaylists.forEach((playlist) => {
      const derivedId = extractPlaylistId(playlist.playlistUrl);
      if (derivedId) {
        ids.add(derivedId);
      }
      if (playlist.id) {
        ids.add(playlist.id);
      }
    });

    return ids;
  }, [existingPlaylists]);

  const displayCategories = useMemo<DisplayCategory[]>(() => {
    if (categories.length === 0) {
      return DEFAULT_CATEGORY_NAMES.map((name, index) => ({
        id: `placeholder-${index}`,
        name,
        icons: [],
        placeholder: true,
      }));
    }

    const normalized = new Map<string, SpotifyCategory>();
    categories.forEach((category) => {
      normalized.set(category.name.toLowerCase(), category);
    });

    const ordered: DisplayCategory[] = DEFAULT_CATEGORY_NAMES.map((name) => {
      const match = normalized.get(name.toLowerCase());
      if (match) {
        return match;
      }
      return { id: `placeholder-${name}`, name, icons: [], placeholder: true };
    });

    const remaining = categories.filter(
      (category) =>
        !DEFAULT_CATEGORY_NAMES.some(
          (name) => name.toLowerCase() === category.name.toLowerCase()
        )
    );

    return [...ordered, ...remaining];
  }, [categories]);

  const selectedCategory = useMemo(
    () =>
      categories.find((category) => category.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId]
  );

  const searchContextMessage = useMemo(() => {
    if (selectedCategory) {
      return `Showing playlists for ${selectedCategory.name}`;
    }

    if (activeQuery) {
      return `Showing results for "${activeQuery}"`;
    }

    return "Search for playlists or choose a category to get started.";
  }, [activeQuery, selectedCategory]);

  const handleSelectCategory = async (category: DisplayCategory) => {
    if (!category.id || category.placeholder) {
      setNotification({
        message: `Category "${category.name}" is not currently available.`,
        type: "error",
      });
      return;
    }

    try {
      await selectCategory(category.id, category.name);
    } catch (err) {
      console.error("Failed to fetch playlists for category", err);
    }
  };

  const handleSelectPlaylist = async (playlistId: string) => {
    try {
      await fetchPlaylistDetailsById(playlistId);
    } catch (err) {
      console.error("Failed to fetch playlist details", err);
    }
  };

  const handleAddPlaylist = () => {
    if (!selectedPlaylist) {
      return;
    }

    if (!selectedPlaylist.public) {
      setNotification({
        message: "Only public playlists can be added.",
        type: "error",
      });
      return;
    }

    if (existingPlaylistIds.has(selectedPlaylist.id)) {
      setNotification({
        message: "This playlist is already in your database.",
        type: "error",
      });
      return;
    }

    const genre = selectedCategory?.name ?? "Any";
    setPendingPlaylist(selectedPlaylist);
    setInitialFormValues(buildPlaylistFormValues(selectedPlaylist, genre));
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setPendingPlaylist(null);
    setInitialFormValues(null);
  };

  const handleConfirmAdd = async (values: SpotifyPlaylistFormValues) => {
    const playlistIdFromUrl = extractPlaylistId(values.playlistUrl);
    if (playlistIdFromUrl && existingPlaylistIds.has(playlistIdFromUrl)) {
      setNotification({
        message: "Playlist already exists in outreach.",
        type: "error",
      });
      return;
    }

    setSaving(true);

    try {
      const payload: Omit<SpotifyPlaylist, "id"> = {
        playlistName: values.playlistName,
        playlistUrl: values.playlistUrl,
        curatorSocialHandle: values.curatorSocialHandle,
        curatorEmail: values.curatorEmail,
        playlistLikes: 0,
        playlistFollowers: Number(values.playlistFollowers) || 0,
        trackCount: Number(values.trackCount) || 0,
        source: values.source,
        genre: values.genre,
        lastUpdated: values.lastUpdated,
      };

      await addOutreachItem<SpotifyPlaylist>(
        OutreachCategory.SPOTIFY_PLAYLISTS,
        payload
      );

      setNotification({
        message: "Playlist added to Spotify Playlists successfully!",
        type: "success",
      });
      onPlaylistAdded();
      handleCloseConfirmation();
    } catch (err) {
      console.error("Failed to add Spotify playlist", err);
      setNotification({
        message: "Failed to add playlist. Please try again.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const selectedPlaylistExists =
    selectedPlaylist && existingPlaylistIds.has(selectedPlaylist.id);
  const disableReason = !selectedPlaylist
    ? undefined
    : selectedPlaylistExists
    ? "Playlist already added"
    : selectedPlaylist.public
    ? undefined
    : "Only public playlists can be added";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "90vw",
            height: "85vh",
            maxWidth: "none",
            maxHeight: "none",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MusicNoteIcon color="primary" />
              <Typography variant="h6">Search Spotify Playlists</Typography>
            </Stack>
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {/* Search & Categories Section */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search Spotify playlists (e.g. 'lofi chill', 'workout hits')"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="search playlists"
                      onClick={handleSearch}
                      edge="end"
                      disabled={loading}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                py: 1,
                px: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.action.hover,
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {displayCategories.map((category) => {
                const isSelected =
                  !category.placeholder && category.id === selectedCategoryId;
                const iconUrl = category.icons?.[0]?.url;

                return (
                  <Chip
                    key={category.id}
                    label={category.name}
                    color={isSelected ? "primary" : "default"}
                    onClick={() => handleSelectCategory(category)}
                    variant={isSelected ? "filled" : "outlined"}
                    icon={
                      iconUrl ? (
                        <Avatar src={iconUrl} alt={category.name} />
                      ) : undefined
                    }
                    disabled={category.placeholder && categoriesLoading}
                    sx={{
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </Box>
            {categoriesLoading && (
              <Typography variant="body2" color="text.secondary">
                Loading categories...
              </Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </Box>

        <Divider />

        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <Grid container sx={{ flexGrow: 1, minHeight: 0 }}>
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                display: "flex",
                flexDirection: "column",
                borderRight: {
                  xs: "none",
                  md: `1px solid ${theme.palette.divider}`,
                },
                minHeight: 0,
              }}
            >
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchContextMessage}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, px: 3, pb: 3, minHeight: 0 }}>
                <SpotifySearchResults
                  playlists={playlists}
                  loading={loading}
                  selectedPlaylistId={selectedPlaylist?.id}
                  onSelectPlaylist={handleSelectPlaylist}
                  existingPlaylistIds={existingPlaylistIds}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: "flex", minHeight: 0 }}>
              <Box sx={{ p: 3, flexGrow: 1 }}>
                <SpotifyPlaylistDetail
                  playlist={selectedPlaylist}
                  loading={detailsLoading}
                  onAdd={handleAddPlaylist}
                  disableAdd={Boolean(disableReason)}
                  disableReason={disableReason}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <ChannelConfirmationModal
        open={confirmationOpen}
        onClose={handleCloseConfirmation}
        mode="spotify"
        category={OutreachCategory.SPOTIFY_PLAYLISTS}
        playlistDetails={pendingPlaylist}
        initialValues={initialFormValues}
        loading={saving}
        onConfirm={handleConfirmAdd}
      />

      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {notification ? (
          <Alert
            onClose={handleCloseNotification}
            severity={notification.type}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};
